import { ASSET_TYPE, FWKIcons, IAsset, ICatalogFilters, ICategory, Logger, Page, SOURCE_TYPE, ToggleButton, getAssetTypeLabel, getCategories, handleRequestError, searchAssets, searchRecommendedAssets, useFeatureIsEnabled } from "@desp-aas/desp-ui-fwk";
import classNames from "classnames";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import Navigation from "../../components/Navigation/Navigation";
import { CatalogResults } from "./CatalogResults/CatalogResults";
import { ReduxState } from "../../redux";
import { setCatalog } from "../../redux/catalogReducer";
import { ICatalogPagination, defaultCatalogFilters, defaultFilters, formatSearchFilters, updateCatalogPagination } from "../../utils/catalog";
import { CatalogFilters } from "./CatalogFilters/CatalogFilters";
import './CatalogPage.css';

const logger = new Logger("component", "CatalogPage");

const newAssetPage = (asset: string) => {
    return `/form/${asset}`
}


export function CatalogPage() {   
    // Asset type, dataset/paper/model/application
    const isGitlabEnabled = useFeatureIsEnabled("gitlab_enabled");
    const { asset_type } = useParams() as { asset_type: ASSET_TYPE };

    // Detailed mode toggle
    const [detailedMode, setDetailedMode] = useState(false);
    const [initState, setInitState] = useState({
        categories: false,
        pagination: false,
        filters: false
    });
    const [initialised, setInitialised] = useState(false);

    const appWidth = useSelector((state: ReduxState)=> state.app.width )
    const { catalogFilters, catalogPagination, catalogSubscriptions } = useSelector((state: ReduxState)=> state.catalog );
    const dispatch = useDispatch()
    
    const [ filters, setFilters ] = useState<ICatalogFilters|undefined>(catalogFilters[asset_type]);
    const [ pagination, setPagination ] = useState<ICatalogPagination|undefined>(catalogPagination[asset_type]);

    // const isMobileMode = () => !!(appWidth && appWidth <= ( asset_type === ASSET_TYPE.course ? 1400 : 1100))
    const isMobileMode = () => !!(appWidth && appWidth <= ( 1100 ))

    const [mobileMode, setMobileMode] = useState<boolean>(isMobileMode());
    useEffect(()=>{ setMobileMode(isMobileMode()) }, [ appWidth ])

    const isScrollMode = () => !!mobileMode;

    const [ scrollMode, setScrollMode ] = useState<boolean>(isScrollMode());
    useEffect(()=>{ setScrollMode(isScrollMode()) }, [ mobileMode, appWidth, asset_type ])

    // FILTERS
    // Filters to be applied. centralized to listen only one variable

    // const [ filters, setFilters ] = useState<ICatalogFilters>({ search: '', sources: [...sourceTypes], categories: [] });

    // list of categories fetched from backend
    const [categories, setCategories] = useState<ICategory[]>();
    // The list of recommended assets
    const [recommendedAssets, setRecommendedAssets] = useState<IAsset[]>();
    // The list of normal assets
    const [assets, setAssets] = useState<IAsset[]>();

    // Using an object because we can trigger useEffect functions even if the value doesn't move
    // const [recommendedAssetOffset, setRecommendedAssetOffset] = useState(0);
    // const [assetOffset, setAssetOffset] = useState(0);
    
    const [recommendedAssetsReachedMax, setRecommendedAssetsReachedMax] = useState(false);
    const [assetsReachedMax, setAssetsReachedMax] = useState(false);

    // total counts returned by the API (for the "N results" line)
    const [recommendedAssetsCount, setRecommendedAssetsCount] = useState<number>();
    const [assetsCount, setAssetsCount] = useState<number>();

    const [loadingAssets, setLoadingAssets] = useState(true);
    const [loadingRecommendedAssets, setLoadingRecommendedAssets] = useState(true);
    
    const [openFilters, setOpenFilters] = useState(false);

    
    const getAssetLimit = useCallback((detailed: boolean) => {
        return detailed || scrollMode ? 20 : 8;
    }, [ asset_type, scrollMode ])
    // detailed ? 50 : 8;

    /* INIT */
    useEffect(() => {
        setInitialised(false);
        setInitState({
            categories: false,
            pagination: false,
            filters: false
        })
        setAssets(undefined);
        setRecommendedAssets(undefined);
        setLoadingAssets(true);
        setLoadingRecommendedAssets(true);
        if (asset_type) {
            fetchCategories()
        }
    }, [asset_type])
    

    useEffect(()=>{
        setInitialised(Object.values(initState).every((v)=> v));
    }, [ initState ])
    
    useEffect(()=>{
        setFilters(catalogFilters[asset_type])
        setInitState((prev)=>({ ...prev, filters: true }))
    }, [ catalogFilters, asset_type ])
    useEffect(()=>{
        setPagination(catalogPagination[asset_type])
        setInitState((prev)=>({ ...prev, pagination: true }))
    }, [ catalogPagination, asset_type ])

    const checkSearchReady = () => {
        return !!(
            filters &&
            filters.categories.length > 0
            && filters.sources.length > 0
            && !!asset_type
        )
    }

    const searchReady = useMemo(()=>{
        let searchReady = checkSearchReady();
        logger.info("searchReady", searchReady)
        return searchReady;
    }, [filters, asset_type, pagination])


    useEffect(() => {
        if(initialised){
            fetchAssets(),
            fetchRecommendedAssets()
        }
    }, [initialised, filters, detailedMode ])

    const fetchCategories = useCallback(()=>{
        getCategories(asset_type !== ASSET_TYPE['course'] ? asset_type as ASSET_TYPE : undefined)
        .then((categories) => { 
            logger.info("Categories result", categories); 
            setCategories(categories);
            let filters = catalogFilters[asset_type];
            if(!filters || filters.categories.length === 0){
                let selectedCategories = categories.map((c) => c.id)
                // selectedCategories = selectedCategories.filter((c)=> filters.categories.includes(c) )
                updateFilters('categories', selectedCategories)
            }
        })
        .catch((e)=>{
            logger.error("get categories error", e)
            handleRequestError(e, { defaultMessage: "An error has occured during category retrieval, please try again later" })
        })
        .finally(()=>{
            setInitState((prev)=>({ ...prev, categories: true }));
        })
    }, [asset_type])


    const updateFilters = useCallback((key: keyof ICatalogFilters, value: any)=>{
        let prev = { ...catalogFilters }

        // dispatch(setCatalog(resetCatalogSubscription(catalogSubscriptions, asset_type)))
        dispatch(setCatalog(updateCatalogPagination(catalogPagination, asset_type, { assetOffset: 0, recommendedAssetOffset: 0 })))
        
        if(!(asset_type in prev)){
            prev[asset_type] = {
                ...defaultFilters(asset_type),
                [key]: value
            }
        }
        else{
            prev[asset_type] = {
                ...prev[asset_type],
                [key]: value
            }
        }
        prev[asset_type].subscription = undefined;

        dispatch(setCatalog({ catalogFilters: prev, catalogSubscriptions: { ...catalogSubscriptions, [asset_type]: undefined } }))
    }, [ catalogFilters, asset_type ])

    // const formatAssets = (assets: IAsset[]) => assets.forEach((a)=> a.type = asset_type )

    const getRecommendedLimit = (_currentOffset: number) => 4;


    const getFilters = useCallback((params: { limit: number, offset: number })=>{
        let { limit, offset } = params;

        return {
            count: true,
            limit: (scrollMode || detailedMode) ? limit + offset : limit, 
            offset: (scrollMode || detailedMode) ? 0 : offset, 

            ...formatSearchFilters({
                search: filters?.search || '',
                type: asset_type,
                categories: filters?.categories || [],
                sources: filters?.sources && filters?.sources.length > 0 ? isGitlabEnabled ? filters?.sources : [ SOURCE_TYPE.external ] : undefined,
                metadata: filters?.metadata,
                geo: filters?.geo
            })
        }
    }, [ asset_type, filters, scrollMode, detailedMode, isGitlabEnabled ])


    // The search is made by the user when the filters are not the ones applied when opening the page
    const isUserSearch = useMemo(()=>{
        if(!filters || !categories) return false;
        const defaults = defaultCatalogFilters(asset_type, categories);
        return !!filters.search
            || !!filters.geo
            || Object.keys(filters.metadata || {}).length > 0
            || filters.categories.length !== defaults.categories.length
            || filters.sources.length !== defaults.sources.length
    }, [ asset_type, filters, categories ])


    async function fetchRecommendedAssets(_offset?: number) {
        console.warn("[searchRecommendedAssets] called")
        if (!checkSearchReady() || !pagination) { 
            console.warn("[searchRecommendedAssets] Search is not ready")
            return setRecommendedAssets(undefined);
        }
        setLoadingRecommendedAssets(true);
        try {
            let offset = _offset === undefined ? pagination?.recommendedAssetOffset : _offset;
            let limit = getRecommendedLimit(offset);
            logger.info("[searchRecommendedAssets] Searching recommended assets", { offset, limit, detailedMode, scrollMode })
            let res = await searchRecommendedAssets(getFilters({ limit, offset }));
            let _assets = res.assets;
            let count = res.count;

            // formatAssets(_assets)
            logger.info("[searchRecommendedAssets] result", _assets)

            logger.info("[searchRecommendedAssets] Updating current")
            if (count <= limit + offset) {
                setRecommendedAssetsReachedMax(true);
            }
            else {
                setRecommendedAssetsReachedMax(false);
            }
            setRecommendedAssetsCount(count)
            setRecommendedAssets(_assets)
        }
        catch (e) {
            logger.error("Error during recommended asset fetch", e)
            handleRequestError(e, { defaultMessage: "An error has occured during recommended asset retrieval, please try again later" })
        }
        finally {
            setLoadingRecommendedAssets(false);
        }
    }

    async function fetchAssets(_offset?: number, append?: boolean) {
        if (!checkSearchReady() || !pagination) {
            return setAssets(undefined);
        }
        setLoadingAssets(true)
        try {
            let offset = _offset === undefined ? pagination?.assetOffset : _offset;
            let limit = getAssetLimit(detailedMode);
            logger.info("[fetchAssets] Searching assets", { offset, limit });

            let res = await searchAssets({
                ...getFilters({ limit, offset }),
                // Browsing the pages of a search is not a new search
                enable_log: (isUserSearch && offset === 0) || undefined
            });
            let _assets = res.assets;
            let count = res.count;

            console.log("_assets", _assets)

            if(_assets){
                // formatAssets(_assets)
                
    
                logger.info("[fetchAssets] results", assets)
    
                if ( count <= limit + offset ) {
                    setAssetsReachedMax(true);
                }
                else {
                    setAssetsReachedMax(false);
                }
                setAssetsCount(count)
                setAssets(_assets)
            }
            else{
                throw new Error('Missing response in search')
            }
            
        }
        catch (e) {
            logger.error("Error during asset fetch", e)
            handleRequestError(e, { defaultMessage: "An error has occured during asset retrieval, please try again later" })
        }
        finally {
            setLoadingAssets(false)
        }
    }



    const recommendedAssetsNext = () => {
        if (!recommendedAssetsReachedMax && pagination) {
            let newOffset = pagination.recommendedAssetOffset + getRecommendedLimit(pagination.recommendedAssetOffset);
            dispatch(setCatalog(updateCatalogPagination(catalogPagination, asset_type, { recommendedAssetOffset: newOffset })))
            fetchRecommendedAssets(newOffset);
        }
    }

    const recommendedAssetsPrevious = () => {
        if ( pagination && pagination.recommendedAssetOffset > 0 ) {
            let newOffset = pagination.recommendedAssetOffset - getRecommendedLimit(pagination.recommendedAssetOffset);
            newOffset = newOffset > 0 ? newOffset : 0;
            dispatch(setCatalog(updateCatalogPagination(catalogPagination, asset_type, { recommendedAssetOffset: newOffset })))
            fetchRecommendedAssets(newOffset)
        }
        else {
            logger.error("[recommendedAssetsPrevious] cannot go previous")
        }
    }

    const assetsNext = () => {
        if (!assetsReachedMax && pagination) {
            let newOffset = pagination.assetOffset + getAssetLimit(detailedMode);
            dispatch(setCatalog(updateCatalogPagination(catalogPagination, asset_type, { assetOffset: newOffset })))
            fetchAssets(newOffset);
        }
    }

    const assetsPrevious = () => {
        if (pagination && pagination.assetOffset > 0) {
            let newOffset = pagination.assetOffset - getAssetLimit(detailedMode);
            newOffset = newOffset > 0 ? newOffset : 0;
            dispatch(setCatalog(updateCatalogPagination(catalogPagination, asset_type, { assetOffset: newOffset })))
            fetchAssets(newOffset);
        }
    }

    const toggleFilter = useCallback(()=>{
        setOpenFilters(!openFilters)
    }, [ openFilters ])

    const closeFilters = useCallback(()=>{
        setOpenFilters(false)
    }, [])
    

    return (
        <Page
            id="catalog-page"
            background2
        >
            <Navigation />
            <div className="catalog-page-wrap">
                <div id="catalog-page-content" className={classNames({ "catalog-filter-mobile": mobileMode, [`asset-type-${asset_type}`]: true })}>
                    {/* <div id="catalog-left-side" > */}
                        <CatalogFilters
                            asset_type={asset_type}
                            loadingAssets={loadingAssets}
                            loadingRecommendedAssets={loadingRecommendedAssets}
                            // filters={filters}
                            updateFilters={updateFilters}
                            searchReady={searchReady}
                            categories={categories || []}
                            mobile={mobileMode}
                        />
                    {/* </div> */}
                    <div id="catalog-right-side">
                        <div id="catalog-right-side-top-banner">
                            {
                                asset_type !== ASSET_TYPE['course'] &&
                                <ToggleButton label="VIEW DETAILED" value={detailedMode} id="catalog-page-toggle-detailed" onToggle={setDetailedMode} />
                            }

                            <Link className="button create-button" id="new-asset" to={newAssetPage(asset_type)}>
                                {FWKIcons.createButton} New {getAssetTypeLabel(asset_type)}
                            </Link>
                        </div>
                        <CatalogResults
                            title="Recommendations"
                            missingFields={!searchReady}
                            assets={recommendedAssets}
                            asset_type={asset_type}
                            loadingCount={4}
                            fourPerRow
                            detailed={detailedMode}
                            eyebrow={getAssetTypeLabel(asset_type)}
                            count={recommendedAssetsCount}
                            offset={(scrollMode || detailedMode) ? 0 : (pagination?.recommendedAssetOffset ?? 0)}
                            loadingAssets={loadingRecommendedAssets}
                            next={recommendedAssetsReachedMax ? undefined : recommendedAssetsNext}
                            previous={(pagination && pagination.recommendedAssetOffset) !== 0 ? recommendedAssetsPrevious : undefined}
                        />
                        <CatalogResults
                            title="Results"
                            missingFields={!searchReady}
                            assets={assets}
                            asset_type={asset_type}
                            loadingCount={8}
                            fourPerRow
                            detailed={detailedMode}
                            eyebrow={getAssetTypeLabel(asset_type)}
                            count={assetsCount}
                            offset={(scrollMode || detailedMode) ? 0 : (pagination?.assetOffset ?? 0)}
                            loadingAssets={loadingAssets}
                            next={assetsReachedMax ? undefined : assetsNext}
                            previous={(pagination && pagination.assetOffset !== 0) ? assetsPrevious : undefined}
                        />
                    </div>
                </div>
            </div>
        </Page>
    )
}